import React, { useEffect, useState, useRef } from "react";
import { RouterProvider } from "react-router";
import { BBUIApp } from "@bbnpm/bb-ui-framework";
import { GlobalStyles } from "./components/global-styles";

import { getServerConfig } from "./api/config";
import { ServerConfig } from "./interfaces/server-config";
import {
  DEFAULT_BOOSTING_MANAGER_LISTS_URL,
  DEFAULT_SCRIBE_BASE_URL,
} from "./utility/server-config-defaults";
import { customRouter } from "./pages";
import { Router } from "@remix-run/router";
import { AnalyticsProvider } from "../contexts/analytics";
import { initializeSentry } from "./sentry";

initializeSentry();

export const App: React.FC = () => {
  const [appRouter, setAppRouter] = useState<Router | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);

  const hasCompletedRef = useRef(false);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    let isMounted = true; // Prevent state updates if component unmounts

    const fetchConfig = async () => {
      if (hasInitializedRef.current) {
        console.log("App already initialized, skipping");
        return;
      }
      hasInitializedRef.current = true;

      try {
        console.log("Fetching server config...");
        setIsLoading(true);
        setConfigError(null);

        const serverConfig: ServerConfig = await getServerConfig(10000); // 10 second timeout
        console.log("Server config received:", serverConfig);

        if (!isMounted || hasCompletedRef.current) return; // Already completed or unmounted

        const router = customRouter(serverConfig);
        setAppRouter(router);
        console.log("Router created successfully with server config");
      } catch (err) {
        console.error("Failed to fetch server config:", err);

        if (!isMounted || hasCompletedRef.current) return; // Already completed or unmounted

        let errorMessage: string;

        // Check if it's a timeout/abort error
        if (
          err instanceof Error &&
          (err.name === "AbortError" || err.name === "TimeoutError")
        ) {
          console.warn("Config fetch timed out, using fallback configuration");
          errorMessage = "Configuration loading timed out, using defaults";
        } else {
          errorMessage =
            err instanceof Error
              ? err.message
              : "Unknown error occurred while loading server configuration. This may be due to network issues, server unavailability, or authentication problems. The application will continue with default settings.";
        }

        setConfigError(errorMessage);

        // Fallback to default config if server config fails
        console.log("Using fallback configuration");
        const fallbackRouter = customRouter({
          payloadSizeLimit: 100,
          websocketChunkSize: 5,
          enableAssetGalleryView: true,
          isOutsideFacing: false,
          boostingManagerListsUrl: DEFAULT_BOOSTING_MANAGER_LISTS_URL,
          scribeBaseUrl: DEFAULT_SCRIBE_BASE_URL,
        });

        setAppRouter(fallbackRouter);
      } finally {
        if (isMounted && !hasCompletedRef.current) {
          setIsLoading(false);
          hasCompletedRef.current = true; // Mark as completed
        }
      }
    };

    fetchConfig();

    // Cleanup function
    return () => {
      isMounted = false;
      hasCompletedRef.current = true; // Prevent any pending operations

      // In React 18 Strict Mode, reset initialization state on unmount
      // so that if the component remounts, it can properly reinitialize
      // Only reset if we haven't actually completed initialization
      if (!appRouter) {
        hasInitializedRef.current = false;
        hasCompletedRef.current = false;
      }
    };
  }, []); // Empty dependency array ensures this only runs once

  if (isLoading) {
    return <div>Loading application...</div>;
  }

  if (!appRouter) {
    return (
      <div>Failed to initialize application. Please refresh the page.</div>
    );
  }

  if (configError) {
    console.warn("Using fallback configuration due to error:", configError);
  }

  return (
    <BBUIApp cssVars>
      <AnalyticsProvider>
        <GlobalStyles />
        <RouterProvider router={appRouter} />
      </AnalyticsProvider>
    </BBUIApp>
  );
};
